import type { LoaderFunctionArgs, MetaFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import { Link, useLoaderData } from '@remix-run/react';
import { buildMeta } from '~/lib/seo';
import { routes } from '~/lib/routes';
import { getEnemyById } from '~/lib/game-data.server';
import { H1, H2, H3, Lead } from '~/components/ui/typography';
import { Container } from '~/components/ui/container';

export const loader = async ({ params }: LoaderFunctionArgs) => {
  const { enemyId } = params;

  if (!enemyId) {
    throw new Response('Enemy ID is required', { status: 400 });
  }

  const enemy = getEnemyById(enemyId);

  if (!enemy) {
    throw new Response('Enemy not found', { status: 404 });
  }

  return json({ enemy });
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data) {
    return buildMeta({
      title: 'Enemy Not Found',
      description: 'The requested enemy could not be found.',
    });
  }

  return buildMeta({
    title: `${data.enemy.name} - Enemies Database`,
    description: data.enemy.description,
  });
};

export default function EnemyDetailPage() {
  const { enemy } = useLoaderData<typeof loader>();

  return (
    <Container size="default" padding="large" className="space-y-8">
      <header className="space-y-3">
        <Link
          to={routes.database.enemies()}
          className="inline-flex items-center text-sm font-semibold text-primary hover:underline"
        >
          ← Back to Enemies
        </Link>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-red-500/10 px-3 py-1 text-xs font-semibold text-red-500">
            {enemy.role}
          </span>
        </div>
        <H1>{enemy.name}</H1>
        <Lead>{enemy.description}</Lead>
      </header>

      {/* Type Tags */}
      {enemy.type_tags.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-6 shadow">
          <H2 className="mb-4">Types</H2>
          <div className="flex flex-wrap gap-2">
            {enemy.type_tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-border bg-background px-3 py-1 text-sm font-medium text-foreground"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Attributes */}
      <div className="rounded-lg border border-border bg-card p-6 shadow">
        <H2 className="mb-4">Attributes</H2>
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Constitution</dt>
            <dd className="mt-1 text-base text-foreground">{enemy.stats.attributes.constitution}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Strength</dt>
            <dd className="mt-1 text-base text-foreground">{enemy.stats.attributes.strength}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Dexterity</dt>
            <dd className="mt-1 text-base text-foreground">{enemy.stats.attributes.dexterity}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Focus</dt>
            <dd className="mt-1 text-base text-foreground">{enemy.stats.attributes.focus}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Intellect</dt>
            <dd className="mt-1 text-base text-foreground">{enemy.stats.attributes.intellect}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Stamina</dt>
            <dd className="mt-1 text-base text-foreground">{enemy.stats.attributes.stamina}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Agility</dt>
            <dd className="mt-1 text-base text-foreground">{enemy.stats.attributes.agility}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Insight</dt>
            <dd className="mt-1 text-base text-foreground">{enemy.stats.attributes.insight}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Wisdom</dt>
            <dd className="mt-1 text-base text-foreground">{enemy.stats.attributes.wisdom}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Charisma</dt>
            <dd className="mt-1 text-base text-foreground">{enemy.stats.attributes.charisma}</dd>
          </div>
        </dl>
      </div>

      {/* Resistances */}
      <div className="rounded-lg border border-border bg-card p-6 shadow">
        <H2 className="mb-4">Resistances</H2>
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Physical</dt>
            <dd className="mt-1 text-base text-foreground">{enemy.stats.resistances.physical}%</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Fire</dt>
            <dd className="mt-1 text-base text-foreground">{enemy.stats.resistances.fire}%</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Ice</dt>
            <dd className="mt-1 text-base text-foreground">{enemy.stats.resistances.ice}%</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Earth</dt>
            <dd className="mt-1 text-base text-foreground">{enemy.stats.resistances.earth}%</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Air</dt>
            <dd className="mt-1 text-base text-foreground">{enemy.stats.resistances.air}%</dd>
          </div>
        </dl>
      </div>

      {/* Presets */}
      {enemy.presets.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-6 shadow">
          <H2 className="mb-4">Skill Presets</H2>
          <div className="space-y-4">
            {enemy.presets.map((preset) => (
              <div key={preset.id} className="rounded-lg border border-border bg-background p-4">
                <H3 className="mb-2">{preset.name}</H3>
                <div className="space-y-1">
                  {preset.skills.map((skill) => (
                    <div key={skill.id} className="text-sm text-muted-foreground">
                      • {skill.name}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Container>
  );
}
